import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'
import { useAuth } from './AuthContext'
import { API_BASE_URL } from '../config/api'

const TournamentContext = createContext()

export const useTournaments = () => {
  const context = useContext(TournamentContext)
  if (!context) {
    throw new Error('useTournaments must be used within a TournamentProvider')
  }
  return context
}

// --- Internal Hook for API Handling ---
function useApi(session) {
    const pendingRequests = useRef(new Map())

    const request = useCallback(async (endpoint, options = {}) => {
        const method = options.method || 'GET'
        const bodyStr = options.body ? JSON.stringify(options.body) : ''
        const requestKey = `${method}:${endpoint}:${bodyStr}`

        if (method === 'GET' && pendingRequests.current.has(requestKey)) {
            return pendingRequests.current.get(requestKey)
        }

        const execute = async () => {
            const url = `${API_BASE_URL}/api/v1${endpoint}`
            const headers = {
                'Content-Type': 'application/json',
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
            }

            const config = {
                ...options,
                headers: { ...headers, ...options.headers },
                body: options.body // Already stringified by caller or body provided
            }
            
            // Ensure body is string if it's an object and not already managed
            if (config.body && typeof config.body !== 'string') {
                config.body = JSON.stringify(config.body)
            }

            const response = await fetch(url, config)
            
            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || `Request failed: ${response.status}`)
            }

            return response.json()
        }

        const promise = execute().finally(() => {
            if (method === 'GET') pendingRequests.current.delete(requestKey)
        })

        if (method === 'GET') {
            pendingRequests.current.set(requestKey, promise)
        }

        return promise
    }, [session])

    return request
}

export const TournamentProvider = ({ children }) => {
  const { session } = useAuth()
  const api = useApi(session)
  
  const [tournaments, setTournaments] = useState([])
  const [featuredTournaments, setFeaturedTournaments] = useState([])
  const [currentTournament, setCurrentTournament] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // --- Helper to wrap API calls with state ---
  const withState = useCallback(async (fn, shouldSetLoading = true) => {
      if (shouldSetLoading) setLoading(true)
      setError(null)
      try {
          return await fn()
      } catch (err) {
          setError(err.message)
          throw err
      } finally {
          if (shouldSetLoading) setLoading(false)
      }
  }, [])

  // --- Actions ---

  const listTournaments = useCallback((status = '', limit = 100) => {
      return withState(async () => {
          const params = new URLSearchParams()
          if (status) params.append('status', status)
          if (limit) params.append('limit', limit.toString())
          
          const data = await api(`/tournaments?${params}`)
          setTournaments(data.tournaments || [])
          return data
      })
  }, [api, withState])

  const getFeaturedTournaments = useCallback((limit = 5) => {
      return withState(async () => {
          const data = await api(`/tournaments/featured?limit=${limit}`)
          setFeaturedTournaments(data.tournaments || [])
          return data
      }, false) // Usually background fetch
  }, [api, withState])

  const createTournament = useCallback((tournamentData) => {
      return withState(async () => {
          const data = await api('/tournaments', { method: 'POST', body: tournamentData })
          listTournaments() // Background refresh
          return data
      })
  }, [api, withState, listTournaments])

  const getTournament = useCallback((tournamentId) => {
      return withState(async () => {
          const data = await api(`/tournaments/${tournamentId}`)
          setCurrentTournament(data)
          return data
      })
  }, [api, withState])

  const joinTournament = useCallback((tournamentId, code = null) => {
      return withState(async () => {
          const options = { method: 'POST' }
          if (code) options.body = { code }
          
          const data = await api(`/tournaments/${tournamentId}/join`, options)
          getTournament(tournamentId) // Refresh details (player list etc)
          listTournaments() // Refresh list status
          return data
      })
  }, [api, withState, getTournament, listTournaments])

  const leaveTournament = useCallback((tournamentId) => {
      return withState(async () => {
          const data = await api(`/tournaments/${tournamentId}/leave`, { method: 'POST' })
          getTournament(tournamentId)
          listTournaments()
          return data
      })
  }, [api, withState, getTournament, listTournaments])

  // Admin Actions
  const startTournament = useCallback((tournamentId) => {
      return withState(async () => {
          const data = await api(`/tournaments/${tournamentId}/start`, { method: 'POST' })
          listTournaments()
          return data
      })
  }, [api, withState, listTournaments])

  const updateTournament = useCallback((tournamentId, updates) => {
      return withState(async () => {
          const data = await api(`/admin/tournaments/${tournamentId}`, { method: 'PUT', body: updates })
          listTournaments()
          return data
      })
  }, [api, withState, listTournaments])

  const cancelTournament = useCallback((tournamentId) => {
      return withState(async () => {
          const data = await api(`/admin/tournaments/${tournamentId}/cancel`, { method: 'POST' })
          listTournaments()
          return data
      })
  }, [api, withState, listTournaments])

  const deleteTournament = useCallback((tournamentId) => {
      return withState(async () => {
          const data = await api(`/admin/tournaments/${tournamentId}`, { method: 'DELETE' })
          listTournaments()
          return data
      })
  }, [api, withState, listTournaments])

  const inviteUserToTournament = useCallback((tournamentId, userId) => {
      return withState(async () => {
          return await api(`/tournaments/${tournamentId}/invite`, { method: 'POST', body: { user_id: userId } })
      })
  }, [api, withState])

  // Data Getters
  const getTournamentStandings = useCallback((tournamentId) => {
      return withState(async () => {
          const data = await api(`/tournaments/${tournamentId}/standings`)
          return data.standings || []
      }, false)
  }, [api, withState])

  const getTournamentBracket = useCallback((tournamentId) => {
      return withState(async () => {
          const data = await api(`/tournaments/${tournamentId}/bracket`)
          return data.pairings || []
      }, false)
  }, [api, withState])

  const getUserTournamentGames = useCallback(() => {
      return withState(async () => {
          if (!session?.access_token) throw new Error('User not authenticated')
          const data = await api('/tournaments/my-games')
          return data.games || []
      }, false)
  }, [api, withState, session?.access_token])

  // Format Helpers (relying on createTournament)
  const createSitAndGo = useCallback((playerCount, tournamentType = 'bracket', timeControl = { minutes: 10, increment: 0 }) => {
    return createTournament({
      name: `Sit & Go ${playerCount} Players`,
      description: `Quick ${playerCount}-player ${tournamentType} tournament`,
      type: tournamentType,
      max_players: playerCount,
      min_players: playerCount,
      time_control: timeControl,
      start_time: null,
    })
  }, [createTournament])

  const createBigTournament = useCallback((name, description, maxPlayers = 0, timeControl = { minutes: 15, increment: 10 }, startTime = null) => {
    return createTournament({
      name,
      description,
      type: 'swiss',
      max_players: maxPlayers,
      min_players: 8,
      time_control: timeControl,
      start_time: startTime,
    })
  }, [createTournament])

  const value = useMemo(() => ({
    // State
    tournaments,
    featuredTournaments,
    currentTournament,
    loading,
    error,

    // Actions
    listTournaments,
    getFeaturedTournaments,
    createTournament,
    updateTournament,
    cancelTournament,
    deleteTournament,
    getTournament,
    joinTournament,
    leaveTournament,
    startTournament,
    inviteUserToTournament,
    getTournamentStandings,
    getTournamentBracket,
    getUserTournamentGames,

    // Helpers
    createSitAndGo,
    createBigTournament,

    // Clear functions
    clearError: () => setError(null),
    clearCurrentTournament: () => setCurrentTournament(null),
  }), [
    tournaments, featuredTournaments, currentTournament, loading, error,
    listTournaments, getFeaturedTournaments, createTournament, updateTournament,
    cancelTournament, deleteTournament, getTournament, joinTournament,
    leaveTournament, startTournament, inviteUserToTournament,
    getTournamentStandings, getTournamentBracket, getUserTournamentGames,
    createSitAndGo, createBigTournament
  ])

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  )
}